import { Component, Input, forwardRef } from '@angular/core';
import { ControlValueAccessor, NG_VALIDATORS, NG_VALUE_ACCESSOR, ValidationErrors, Validator } from '@angular/forms';
import { DEFAULT_COUNTRY_CODE, combineE164, normalizeCountryCode, phoneExampleForCode, splitE164 } from '@core/utils/phone.util';

/**
 * Reusable phone-number field: two manual inputs — a country code (e.g. "+44") and a
 * national number — with a live example for whatever country code is entered.
 *
 * It is a ControlValueAccessor whose value is the combined E.164 string (or null when
 * empty/incomplete), so it drops into any reactive form that binds a single phone control:
 *   <app-phone-number-input formControlName="e164phoneNumber"></app-phone-number-input>
 */
@Component({
  selector: 'app-phone-number-input',
  templateUrl: './phone-number-input.component.html',
  styleUrl: './phone-number-input.component.scss',
  standalone: false,
  providers: [
    { provide: NG_VALUE_ACCESSOR, useExisting: forwardRef(() => PhoneNumberInputComponent), multi: true },
    { provide: NG_VALIDATORS, useExisting: forwardRef(() => PhoneNumberInputComponent), multi: true }
  ]
})
export class PhoneNumberInputComponent implements ControlValueAccessor, Validator {
  @Input() label = 'Phone Number';

  countryCode = DEFAULT_COUNTRY_CODE;
  nationalNumber = '';
  disabled = false;
  touched = false;

  private onChange: (value: string | null) => void = () => {};
  private onTouched: () => void = () => {};

  writeValue(value: string | null): void {
    if (value) {
      const parts = splitE164(value);
      this.countryCode = parts.countryCode || DEFAULT_COUNTRY_CODE;
      this.nationalNumber = parts.nationalNumber;
    } else {
      this.countryCode = DEFAULT_COUNTRY_CODE;
      this.nationalNumber = '';
    }
  }

  registerOnChange(fn: (value: string | null) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  validate(): ValidationErrors | null {
    const national = (this.nationalNumber || '').trim();
    if (!national) {
      return null; // empty -> valid (phone is optional)
    }
    return normalizeCountryCode(this.countryCode) ? null : { incompletePhone: true };
  }

  onFieldChange(): void {
    this.onChange(combineE164(this.countryCode, this.nationalNumber) ?? null);
  }

  onBlur(): void {
    this.touched = true;
    this.onTouched();
  }

  get example(): string {
    return phoneExampleForCode(this.countryCode);
  }

  get showIncompleteError(): boolean {
    return this.touched && !!(this.nationalNumber || '').trim() && !normalizeCountryCode(this.countryCode);
  }
}
